/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { Button, Col, Container, Row, Table, Card } from 'react-bootstrap'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import * as Common from '../../utils/Common'
import api from '../../config/AxiosInstance'
import { transformKey } from '../../helpers/constants'
import { FileText, Building2, Copy } from 'lucide-react'
import Loading from '../../components/atoms/Loading'
import { Link } from 'react-router-dom'

const labelDetailStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const valueDetailStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#333333',
  marginBottom: '0'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

function MaterialIssueDetailPage() {
  const { id } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingDownload, setLoadingDownload] = useState(false)
  const [loadingSendToErp, setLoadingSendToErp] = useState(false)

  const getData = async () => {
    try {
      setLoading(true)
      const response = await api.get(`material-issue/${id}`)
      const { data } = response.data
      setData(data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadFileAttachment = (attachment) => {
    window.open(attachment, '_blank')
  }

  const handleDownloadBeritaAcara = async (eventId) => {
    try {
      setLoadingDownload(true)
      const res = await api.get(`/berita-acara/${eventId}`, {
        responseType: 'arraybuffer'
      })
      const { status, data } = res
      if (status === 200) {
        const blob = new Blob([data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `berita_acara_${eventId}.pdf`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const err = error?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(err)
    } finally {
      setLoadingDownload(false)
    }
  }

  const handleRetrySendToErp = async () => {
    try {
      setLoadingSendToErp(true)
      const payload = {
        module: 'material_issue',
        id: id
      }
      const res = await api.post('master/stocks/send-erp', payload)
      const { status, data } = res
      if (status === 200) {
        if (!data.erp_code) {
          throw new Error('Failed to send erp')
        }
        toast.success('Send to erp success', {
          position: 'top-right',
          style: {
            fontSize: '14px',
            fontWeight: '600'
          }
        })
        getData()
      }
    } catch (error) {
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoadingSendToErp(false)
    }
  }

  const columns = [
    {
      key: 'item_code_name',
      label: 'Item Code - Name',
      component: (data) => data.item_code + ' - ' + data.item_name
    },
    {
      key: 'batch',
      label: 'Batch',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          {data.batch ?? '-'}

          {data.batch && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(data.batch)
                toast.success('Copied to clipboard')
              }}
              title="Copy"
            />
          )}
        </div>
      )
    },
    { key: 'total_pack', label: 'Qty Kemasan' },
    { key: 'total_qty', label: 'Base Qty UOM' },
    {
      key: 'slot',
      label: 'Location',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          {data.slot ?? '-'}

          {data.slot && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(data.slot)
                toast.success('Copied to clipboard')
              }}
              title="Copy"
            />
          )}
        </div>
      )
    },
    {
      key: 'problem',
      label: 'Problem',
      component: (data) => transformKey(data.problem)
    },
    {
      key: 'bu',
      label: 'BU',
      component: (data) => data.bu ?? '-'
    }
  ]

  useEffect(() => {
    if (id) {
      getData()
    }
  }, [id])

  if (loading) {
    return <Loading />
  }

  return (
    <Container fluid className="px-4 mb-5">
      <Toaster />
      <Row>
        <Col md={12} className="mt-3 mb-4">
          <div className="d-flex flex-row justify-content-between">
            <div className="d-flex flex-column justify-content-start">
              <h6 className="header-title-style" style={{ fontSize: '18px' }}>
                {state?.code}
              </h6>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb fs-6 mb-0">
                  <li className="breadcrumb-item">
                    <Link
                      to="/material-issue"
                      state={state}
                      className="text-decoration-none"
                    >
                      <span className="text-black fw-bold text-uppercase">
                        Material Issue
                      </span>
                    </Link>
                  </li>
                  <li
                    className="breadcrumb-item sj-item active"
                    aria-current="page"
                  >
                    {state?.code}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex gap-2 justify-content-end">
              {!data?.erp_code && (
                <Button
                  className={`btn-primary-blue d-flex align-items-center gap-2 ${
                    loadingSendToErp ? 'disabled' : ''
                  }`}
                  onClick={() => handleRetrySendToErp()}
                  style={{ height: '35px' }}
                >
                  {loadingSendToErp ? 'Sending...' : 'Send To ERP'}
                </Button>
              )}
              <Button
                className={`btn-primary-orange d-flex align-items-center gap-2`}
                onClick={() => handleDownloadBeritaAcara(data?.event_id)}
                disabled={loadingDownload}
                style={{ height: '35px' }}
              >
                <FileText size={16} />
                {loadingDownload ? 'Downloading...' : 'Berita Acara'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <h5
            className="mb-3 text-secondary fw-bold"
            style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
          >
            Items ({data?.items?.length || 0})
          </h5>
        </Col>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm bg-white"
            style={{
              fontSize: '0.9rem',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                {columns.map((column) => (
                  <th
                    className="p-2 align-middle"
                    key={column.key}
                    style={tableHeaderStyle}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.items.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="p-4 text-center text-muted"
                  >
                    No items found
                  </td>
                </tr>
              )}
              {!loading &&
                data?.items?.length > 0 &&
                data?.items.map((item, indexItem) => (
                  <tr
                    key={indexItem}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="p-2 align-middle text-dark"
                      >
                        {column.component && column.component(item)}
                        {!column.component && (item[column.key] || '-')}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}>
          <Card style={cardStyle}>
            <div className="d-flex align-items-center">
              <div
                className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                style={{ width: '48px', height: '48px' }}
              >
                <Building2 size={24} className="text-secondary" />
              </div>
              <div>
                <div style={labelDetailStyle}>ERP DOC NO</div>
                <div style={valueDetailStyle}>
                  {data?.erp_code || 'Not Generated'}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {data?.attachment?.length > 0 && (
        <div className="mb-5">
          <h5
            className="mb-3 text-secondary fw-bold"
            style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
          >
            Attachments
          </h5>
          <Row className="g-3">
            {data?.attachment?.map((attachment, index) => (
              <Col key={attachment.id} md={4}>
                <Card className="h-100 border-0 shadow-sm attachment-card">
                  <Card.Body className="d-flex align-items-center p-3">
                    <div
                      className="bg-light p-3 rounded me-3 d-flex align-items-center justify-content-center"
                      style={{ width: '48px', height: '48px' }}
                    >
                      <FileText className="text-secondary fs-4" size={24} />
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div
                        className="text-truncate fw-medium"
                        title={attachment.attachment?.split('/').pop()}
                        style={{ fontSize: '0.9rem' }}
                      >
                        {attachment.attachment?.split('/').pop() || 'File'}
                      </div>
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none text-primary-orange"
                        style={{ fontSize: '0.8rem' }}
                        onClick={() =>
                          handleDownloadFileAttachment(attachment.attachment)
                        }
                      >
                        Download
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}
    </Container>
  )
}

export default MaterialIssueDetailPage
